import { useEffect, useState, useCallback } from 'react';
import { useUIStore } from '@stores/v4/useUIStore';
import { useSessionStore } from '@stores/v4/useSessionStore';
import { useHistoryStore } from '@stores/v4/useHistoryStore';
import { useRecoveryStore } from '@stores/v4/useRecoveryStore';
import { useProgramManifestStore } from '@stores/v4/useProgramManifestStore';
import { useComposerStore } from '@stores/v4/useComposerStore';
import { useCalendarStore } from '@stores/v4/useCalendarStore';
import { useSessionTimer } from '../shared/hooks/use-session-timer';
import { EventManager } from '../services/event-manager.service';
import { DB } from '../services/database.service';
import { Event } from '@/types/zod.schemas';
import { EVENT_TYPES } from '@shared/constants/event-types.v4';

interface UseMasterPlanRouterProps {
    currentView: string | null;
    onNavigate: (view: string | null, state?: any) => void;
    lastView: string | null;
}

/**
 * useMasterPlanRouter: Headless navigation and global modal orchestrator.
 * REFACTORED: Centralizes all drawer menu callbacks to reduce View complexity (< 150 lines).
 * HARDENED: Direct store access for 0ms reactivity.
 */
export const useMasterPlanRouter = ({ currentView, onNavigate, lastView }: UseMasterPlanRouterProps) => {
    // 1. SELECTORS
    const activeSession = useSessionStore(state => state.activeSession);
    const startSession = useSessionStore(state => state.startSession);
    const finishSession = useSessionStore(state => state.finishSession);
    const retroactiveDate = useSessionStore(state => state.retroactiveDate);
    const getSessionProgress = useSessionStore(state => state.getSessionProgress);
    const getPerformanceLabel = useSessionStore(state => state.getPerformanceLabel);
    const resetSession = useSessionStore(state => state.resetSession);
    const syncFocus = useSessionStore(state => state.syncFocus);

    const activeRecoverySession = useRecoveryStore(state => state.activeRecoverySession);
    const startRecovery = useRecoveryStore(state => state.startRecovery);
    const endRecovery = useRecoveryStore(state => state.endRecovery);
    const resetRecovery = useRecoveryStore(state => state.resetRecovery);

    const fetchGlobalHistory = useHistoryStore(state => state.fetchGlobalHistory);
    const fetchUniqueExercises = useHistoryStore(state => state.fetchUniqueExercises);
    const activeHistorySession = useHistoryStore(state => state.activeHistorySession);

    const isComposerOpen = useComposerStore(state => state.isComposerOpen);
    const resetComposer = useComposerStore(state => state.resetComposer);
    const openComposer = useComposerStore(state => state.openComposer);

    const { rescheduleQuick, rescheduleEvent } = useCalendarStore();

    const { 
        setLastView, setMetabolicViewActive, isMetabolicViewActive, 
        maximizeRecovery, maximizeSession, setLastSelectedDate 
    } = useUIStore();

    // 2. TIMERS
    const elapsed = useSessionTimer(activeSession, retroactiveDate);
    const sleepElapsed = useSessionTimer(activeRecoverySession);

    // 3. MODAL STATE
    const [showAbandonModal, setShowAbandonModal] = useState(false);
    const [showFinishModal, setShowFinishModal] = useState(false);
    const [confirmDeleteLog, setConfirmDeleteLog] = useState<{eventId: string, subId?: string, type: string} | null>(null); 
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [showSleepConfirm, setShowSleepConfirm] = useState(false);
    const [showWakeUpConfirm, setShowWakeUpConfirm] = useState(false);
    const [showSessionConflict, setShowSessionConflict] = useState(false);
    const [showCloneConfirm, setShowCloneConfirm] = useState<{ event: Event, date: Date } | null>(null);

    // 4. DERIVATIONS
    const { percent: globalPercent } = getSessionProgress();
    const performanceLabel = getPerformanceLabel();

    // 5. EFFECTS
    useEffect(() => {
        const needsHistory = currentView === 'master-agenda' || (currentView === null && lastView === 'master-agenda');
        if (needsHistory) {
            fetchGlobalHistory();
            fetchUniqueExercises();
        }
    }, [currentView, lastView, fetchGlobalHistory, fetchUniqueExercises]);

    useEffect(() => {
        if (currentView && currentView !== 'session' && currentView !== 'builder') setLastView(currentView);
    }, [currentView, setLastView]);

    // 6. CORE HANDLERS
    const handleAbandonSession = useCallback(() => { 
        resetSession(); resetRecovery(); resetComposer();
        setShowAbandonModal(false); 
        onNavigate(lastView || 'master-agenda'); 
    }, [resetSession, resetRecovery, resetComposer, onNavigate, lastView]);
    
    const handleFinalizeSession = useCallback(async () => {
        setShowFinishModal(false);
        setIsSaving(true);
        try { await finishSession(); onNavigate(lastView || 'master-agenda'); }
        catch (e: any) { alert("Error saving: " + e.message); }
        finally { setIsSaving(false); }
    }, [finishSession, onNavigate, lastView]);
    
    const handleDeleteLog = useCallback(async () => {
        if (!confirmDeleteLog) return;
        const payload = { ...confirmDeleteLog };
        setConfirmDeleteLog(null);
        setIsDeleting(true);
        try {
            await EventManager.deleteCitizenEvent(payload.eventId, payload.subId, payload.type);
        }
        catch (e: any) { alert("Delete failed: " + e.message); }
        finally { setIsDeleting(false); }
    }, [confirmDeleteLog]);

    const handleToggleSleep = useCallback(() => {
        if (activeRecoverySession) setShowWakeUpConfirm(true);
        else if (activeSession) setShowSessionConflict(true);
        else setShowSleepConfirm(true);
    }, [activeRecoverySession, activeSession]);

    const handleInitiateSleep = useCallback(() => {
        startRecovery(null);
        setShowSleepConfirm(false);
    }, [startRecovery]);

    const handleConfirmWakeUp = useCallback(async () => {
        setShowWakeUpConfirm(false);
        setIsSaving(true);
        try { 
            await endRecovery(); 
            onNavigate(lastView || 'master-agenda');
        } 
        catch (e: any) { alert("Sync failed: " + e.message); } 
        finally { setIsSaving(false); }
    }, [endRecovery, onNavigate, lastView]);

    const handleConfirmClone = useCallback(async () => {
        if (!showCloneConfirm) return;
        const { event, date } = showCloneConfirm;
        setShowCloneConfirm(null);
        setIsSaving(true);
        try { await EventManager.cloneEvent(event, date); } 
        catch (e: any) { alert("Clone failed: " + e.message); } 
        finally { setIsSaving(false); }
    }, [showCloneConfirm]);

    const handlePrepareActivity = (selectedDate: Date) => {
        if (activeRecoverySession || activeSession) {
            setShowSessionConflict(true);
            return;
        }
        const isToday = selectedDate.toDateString() === new Date().toDateString();
        const dateStr = isToday ? null : selectedDate.toISOString();
        useSessionStore.setState({ retroactiveDate: dateStr });
        onNavigate('library');
    };

    const handleStartPlannedSession = async (event: Event, retroDate?: string | null) => {
        if (activeSession?.p_consume_event_id === event.id || (activeRecoverySession as any)?.p_consume_event_id === event.id) {
            syncFocus(); 
            if (activeSession) maximizeSession();
            if (activeRecoverySession) maximizeRecovery();
            onNavigate('session');
            return;
        }
        if (activeRecoverySession || activeSession) {
            setShowSessionConflict(true);
            return;
        }
        const dateArg = retroDate !== undefined ? retroDate : (new Date(event.start_time).toDateString() === new Date().toDateString() ? null : new Date(event.start_time).toISOString());
        const dayId = event.program_day_id;
        if (!dayId) return;
        await startSession(dayId, dateArg, event.id);
        onNavigate('session'); 
    };

    // --- DRAWER MENU CALLBACKS ---
    const handleReschedule = async (eventId: string, typeOrDate: string) => {
        if (['tomorrow', 'next_week', 'today'].includes(typeOrDate)) {
            const date = new Date();
            if (typeOrDate === 'tomorrow') date.setDate(date.getDate() + 1);
            if (typeOrDate === 'next_week') date.setDate(date.getDate() + 7);
            await EventManager.rescheduleEvent(eventId, toLogicDate(date));
        } else {
            await EventManager.rescheduleEvent(eventId, typeOrDate);
        }
    };

    const handleManualLog = async (event: Event) => {
        const dateArg = new Date(event.start_time).toISOString();
        await startSession(event.program_day_id!, dateArg, event.id, true);
        onNavigate('session');
    };

    const handleMarkMissed = async (eventId: string) => {
        await DB.updateUnifiedEventStatus(eventId, 'SKIPPED');
        await fetchGlobalHistory(true);
    };

    const handleMarkCompleted = async (eventId: string) => {
        await DB.updateUnifiedEventStatus(eventId, 'COMPLETED');
        await fetchGlobalHistory(true);
    };

    // --- QUICK HEADER ACTIONS ---
    const handleQuickMetabolic = () => setMetabolicViewActive(!isMetabolicViewActive);
    const handleQuickNutrition = () => openComposer(new Date(), { type: EVENT_TYPES.NUTRITION });
    const handleQuickSupplement = (window?: string, scheduledTime?: Date) => {
        const title = window ? window.replace('_', ' ') + " STACK" : undefined;
        openComposer(scheduledTime || new Date(), { 
            type: EVENT_TYPES.SUPPLEMENTATION, 
            window,
            status: window ? 'COMPLETED' : 'PLANNED',
            label: title
        });
    };

    return {
        activeSession, activeRecoverySession, elapsed, sleepElapsed, isComposerOpen, globalPercent, performanceLabel,
        showAbandonModal, setShowAbandonModal, showFinishModal, setShowFinishModal, confirmDeleteLog, setConfirmDeleteLog,
        showSleepConfirm, setShowSleepConfirm, showWakeUpConfirm, setShowWakeUpConfirm, showSessionConflict, setShowSessionConflict,
        showCloneConfirm, setShowCloneConfirm, isSaving, isDeleting,
        handleAbandonSession, handleFinalizeSession, handleDeleteLog, handleToggleSleep, handleInitiateSleep, 
        handleConfirmWakeUp, handleConfirmClone, handlePrepareActivity, handleStartPlannedSession, maximizeRecovery, maximizeSession,
        handleQuickMetabolic, handleQuickNutrition, handleQuickSupplement,
        // Drawer Menu
        handleReschedule, handleManualLog, handleMarkMissed, handleMarkCompleted
    };
};
